/**
 * src/state/actions/useBcdrActions.js
 *
 * Business-impact-analysis (BIA) and BC/DR action callbacks extracted
 * from useAppState.js as part of the file-splitting refactor.
 *
 * BIA persists through assets-write (ADR-0005). The six BC/DR collections
 * (bcdrPlans, bcdrRunbooks, bcdrRunbookRuns, bcdrBackups, bcdrDrTests,
 * bcdrPenTests) persist through the dedicated bcdr-write per-record store
 * (ADR-0006, BCA-03) instead of the whole-tenant-state blob, so an unrelated
 * BC/DR record created in one tab can no longer discard a concurrent edit to
 * a different record in another tab (#4845 — DB-CONC-002). Same optimistic-
 * update-then-reconcile shape as saveBIA/useRegistryActions.saveVendor.
 *
 * Sub-hook: useBcdrActions({ updateState, tenantId, flushPendingSave })
 *
 * Returns:
 *   saveBIA, deleteBIA, overrideAssetCriticality,
 *   saveBcdrPlan, deleteBcdrPlan,
 *   saveBcdrRunbook, deleteBcdrRunbook,
 *   saveBcdrRunbookRun, deleteBcdrRunbookRun,
 *   saveBcdrBackup, deleteBcdrBackup,
 *   saveBcdrDrTest, deleteBcdrDrTest,
 *   saveBcdrPenTest, deleteBcdrPenTest
 *
 * Every save/delete below is now async — it resolves once the server has
 * acknowledged the write (or rejects, with the optimistic local update
 * already rolled back). Callers must await it (or handle the rejection) to
 * know the write actually landed — see usePromiseSaveStatus.js for the
 * shared UI pattern the BC/DR modals use.
 */

import { useCallback } from 'react';
import { logActivity } from '../../api/activityLogger.js';
import { emitEngineEvent } from '../../engine/engineEventBus.js';
import * as assetApi from '../../api/assetApi.js';
import * as bcdrApi from '../../api/bcdrApi.js';
import {
  captureRecord,
  restoreRecord,
  beginRecordWrite,
  markRecordCommitted,
} from './optimisticRollback.js';
import { queueNetworkWrite } from '../networkRetryQueue.js';

// ADR-0005 §2.3(c) (msr 2.5d): a BIA save or delete can change the linked
// asset's criticality on the server, in the same transaction. The response
// carries those assets (with their new rowVersion); adopt them so the register
// shows the value the server holds and the next asset save's version check
// compares against the right row.
function adoptDerivedAssets(s, assets) {
  if (!Array.isArray(assets) || assets.length === 0) return;
  if (!s.assets) s.assets = [];
  for (const a of assets) {
    if (!a || !a.id) continue;
    const idx = s.assets.findIndex((x) => x.id === a.id);
    if (idx >= 0) s.assets[idx] = a;
  }
}

export function useBcdrActions({ updateState, tenantId }) {
  // Persists through the dedicated assets-write per-record store (ADR-0005)
  // instead of the whole-tenant-state blob, so an unrelated BIA created in
  // one tab can no longer discard a concurrent edit to a different BIA in
  // another tab (#4852 — DB-CONC-002). Same optimistic-update-then-reconcile
  // shape as useRegistryActions.saveVendor/saveAsset.
  const saveBIA = useCallback(
    async (bia, session) => {
      let isEdit = false;
      let before = null;
      // #5423's draft-autosave-vs-terminal-save race is serialised in
      // BIAWizard, but any other pair of concurrent writers for the same BIA
      // hits the same rollback hazard — see optimisticRollback.js's
      // "Write fences" note.
      const fenceKey = `bia:${bia.id}`;
      const fence = beginRecordWrite(fenceKey);
      updateState((s) => {
        if (!s.biaAssessments) s.biaAssessments = [];
        const idx = s.biaAssessments.findIndex((b) => b.id === bia.id);
        isEdit = idx >= 0;
        before = captureRecord(s.biaAssessments, bia.id);
        if (isEdit) s.biaAssessments[idx] = bia;
        else s.biaAssessments = [bia, ...s.biaAssessments];
      });

      // biaAssessments is stripped from the blob by state-write, so a refused
      // assets-write is never rescued by the debounced save — roll the record
      // back rather than show a BIA the server never stored (see
      // optimisticRollback.js).
      const reconcile = (r) => {
        markRecordCommitted(fenceKey);
        if (!r?.bia) return;
        updateState((s) => {
          const idx = (s.biaAssessments || []).findIndex((b) => b.id === r.bia.id);
          if (idx >= 0) s.biaAssessments[idx] = r.bia;
          else s.biaAssessments = [r.bia, ...(s.biaAssessments || [])];
          adoptDerivedAssets(s, r.assets);
        });
      };
      let result;
      try {
        result = await assetApi.saveBiaRecord(bia, tenantId);
      } catch (e) {
        // Same shape as saveAsset: roll back so the list never shows a BIA the
        // server has not stored, then retry only a write that never reached
        // the server. A BIA is an input to criticality and continuity
        // planning — losing one biases the compliance picture toward fewer
        // gaps, which is the direction that must never happen silently
        // (#5253).
        updateState((s) => {
          s.biaAssessments = restoreRecord(s.biaAssessments, bia.id, before, { fence });
        });
        const queued = queueNetworkWrite({
          key: `bia:${bia.id}`,
          error: e,
          attempt: () => assetApi.saveBiaRecord(bia, tenantId),
          onSuccess: (r) => {
            reconcile(r || { bia });
            emitEngineEvent('bia.saved', { bia });
            if (session) {
              logActivity(tenantId, session, {
                module: 'BIA',
                actionType: isEdit ? 'edited' : 'created',
                recordId: bia.id,
                recordLabel: bia.assetName,
              });
            }
          },
        });
        e.willRetry = queued;
        throw e;
      }
      reconcile(result);

      emitEngineEvent('bia.saved', { bia });

      if (session) {
        logActivity(tenantId, session, {
          module: 'BIA',
          actionType: isEdit ? 'edited' : 'created',
          recordId: bia.id,
          recordLabel: bia.assetName,
        });
      }
      // GH #7191: the caller needs the server's answer — the recomputed
      // rating and any criticalityConflicts (BIA-B-002) to show.
      return result;
    },
    [updateState, tenantId]
  );

  // GH #7191 (BIA-B-002): the attributed manual criticality override, or its
  // revert to the BIA value. Not optimistic — the value is server-owned
  // (AST-B-006), so the register shows only what the server stored.
  const overrideAssetCriticality = useCallback(
    async (assetId, { criticality, reason, revert = false, rowVersion } = {}) => {
      const body = revert ? { assetId, revert: true, reason } : { assetId, criticality, reason };
      if (rowVersion !== undefined) body.rowVersion = rowVersion;
      const result = await assetApi.overrideAssetCriticality(body, tenantId);
      if (result?.asset) updateState((s) => adoptDerivedAssets(s, [result.asset]));
      return result;
    },
    [updateState, tenantId]
  );

  const deleteBIA = useCallback(
    async (id, label, session) => {
      let before = null;
      const fenceKey = `bia:${id}`;
      const fence = beginRecordWrite(fenceKey);
      updateState((s) => {
        before = captureRecord(s.biaAssessments, id);
        s.biaAssessments = (s.biaAssessments || []).filter((b) => b.id !== id);
      });

      let result;
      try {
        result = await assetApi.deleteBiaRecord(id, tenantId);
      } catch (e) {
        updateState((s) => {
          s.biaAssessments = restoreRecord(s.biaAssessments, id, before, { fence });
        });
        throw e;
      }
      markRecordCommitted(fenceKey);
      if (result?.assets?.length) updateState((s) => adoptDerivedAssets(s, result.assets));

      if (session) {
        logActivity(tenantId, session, {
          module: 'BIA',
          actionType: 'deleted',
          recordId: id,
          recordLabel: label || id,
        });
      }
    },
    [updateState, tenantId]
  );

  /**
   * Build a save/delete pair for one BC/DR collection. All six collections
   * share an identical optimistic-update-then-reconcile shape (mirroring
   * saveBIA/deleteBIA above), so a factory avoids six near-duplicate
   * hand-written closures while keeping each collection's behaviour
   * traceable through this one function.
   *
   *   stateKey    — state.<key> array this collection lives under
   *   bodyKey     — the field name bcdr-write's response carries the
   *                 record under (matches azure-functions/bcdr-write's
   *                 COLLECTIONS config exactly)
   *   saveFn      — bcdrApi.saveXxxRecord
   *   deleteFn    — bcdrApi.deleteXxxRecord
   *   labelOf     — (record) => the activity-log recordLabel
   *   activityKind— the module tag appendActivityLog / logActivity groups by
   */
  function makeCollectionActions({ stateKey, bodyKey, saveFn, deleteFn, labelOf }) {
    const save = async (record, session, options) => {
      let isEdit = false;
      let before = null;
      const fenceKey = `${stateKey}:${record.id}`;
      const fence = beginRecordWrite(fenceKey);
      updateState((s) => {
        if (!s[stateKey]) s[stateKey] = [];
        const idx = s[stateKey].findIndex((r) => r.id === record.id);
        isEdit = idx >= 0;
        before = captureRecord(s[stateKey], record.id);
        if (isEdit) s[stateKey][idx] = record;
        else s[stateKey] = [record, ...s[stateKey]];
      });

      const reconcile = (r) => {
        markRecordCommitted(fenceKey);
        const serverRecord = r?.[bodyKey];
        if (!serverRecord) return;
        updateState((s) => {
          const idx = (s[stateKey] || []).findIndex((x) => x.id === serverRecord.id);
          if (idx >= 0) s[stateKey][idx] = serverRecord;
          else s[stateKey] = [serverRecord, ...(s[stateKey] || [])];
        });
      };

      const logSave = () => {
        if (!session) return;
        logActivity(tenantId, session, {
          module: 'BCDR',
          actionType: isEdit ? 'edited' : 'created',
          recordId: record.id,
          recordLabel: labelOf(record),
        });
      };

      let result;
      try {
        result = await saveFn(record, tenantId, options);
      } catch (e) {
        // A refused write must never leave the optimistic record on screen —
        // this collection is authoritative in the per-record store once
        // migrated, so no ambient blob save will ever rescue it (mirrors
        // saveBIA's rollback reasoning).
        updateState((s) => {
          s[stateKey] = restoreRecord(s[stateKey], record.id, before, { fence });
        });
        const queued = queueNetworkWrite({
          key: fenceKey,
          error: e,
          attempt: () => saveFn(record, tenantId),
          onSuccess: (r) => {
            reconcile(r || { [bodyKey]: record });
            logSave();
          },
        });
        e.willRetry = queued;
        throw e;
      }
      reconcile(result);
      logSave();
      return result;
    };

    const del = async (id, label, session) => {
      let before = null;
      const fenceKey = `${stateKey}:${id}`;
      const fence = beginRecordWrite(fenceKey);
      updateState((s) => {
        before = captureRecord(s[stateKey], id);
        s[stateKey] = (s[stateKey] || []).filter((r) => r.id !== id);
      });

      try {
        await deleteFn(id, tenantId);
      } catch (e) {
        updateState((s) => {
          s[stateKey] = restoreRecord(s[stateKey], id, before, { fence });
        });
        throw e;
      }
      markRecordCommitted(fenceKey);

      if (session) {
        logActivity(tenantId, session, {
          module: 'BCDR',
          actionType: 'deleted',
          recordId: id,
          recordLabel: label || id,
        });
      }
    };

    return { save, del };
  }

  const plan = makeCollectionActions({
    stateKey: 'bcdrPlans',
    bodyKey: 'plan',
    saveFn: bcdrApi.saveBcdrPlanRecord,
    deleteFn: bcdrApi.deleteBcdrPlanRecord,
    labelOf: (r) => r.title,
  });
  const runbook = makeCollectionActions({
    stateKey: 'bcdrRunbooks',
    bodyKey: 'runbook',
    saveFn: bcdrApi.saveBcdrRunbookRecord,
    deleteFn: bcdrApi.deleteBcdrRunbookRecord,
    labelOf: (r) => r.title,
  });
  const run = makeCollectionActions({
    stateKey: 'bcdrRunbookRuns',
    bodyKey: 'run',
    saveFn: bcdrApi.saveBcdrRunRecord,
    deleteFn: bcdrApi.deleteBcdrRunRecord,
    labelOf: (r) => r.runbookTitle || r.runbookId,
  });
  const backup = makeCollectionActions({
    stateKey: 'bcdrBackups',
    bodyKey: 'backup',
    saveFn: bcdrApi.saveBcdrBackupRecord,
    deleteFn: bcdrApi.deleteBcdrBackupRecord,
    labelOf: (r) => r.assetName,
  });
  const drTest = makeCollectionActions({
    stateKey: 'bcdrDrTests',
    bodyKey: 'drTest',
    saveFn: bcdrApi.saveBcdrDrTestRecord,
    deleteFn: bcdrApi.deleteBcdrDrTestRecord,
    labelOf: (r) => r.title,
  });
  const penTest = makeCollectionActions({
    stateKey: 'bcdrPenTests',
    bodyKey: 'penTest',
    saveFn: bcdrApi.saveBcdrPenTestRecord,
    deleteFn: bcdrApi.deleteBcdrPenTestRecord,
    labelOf: (r) => r.title,
  });

  const saveBcdrPlan = useCallback(plan.save, [updateState, tenantId]);
  const deleteBcdrPlan = useCallback(plan.del, [updateState, tenantId]);
  const saveBcdrRunbook = useCallback(runbook.save, [updateState, tenantId]);
  const deleteBcdrRunbook = useCallback(runbook.del, [updateState, tenantId]);
  const saveBcdrRunbookRun = useCallback(run.save, [updateState, tenantId]);
  const deleteBcdrRunbookRun = useCallback(run.del, [updateState, tenantId]);
  const saveBcdrBackup = useCallback(backup.save, [updateState, tenantId]);
  const deleteBcdrBackup = useCallback(backup.del, [updateState, tenantId]);
  const saveBcdrDrTest = useCallback(drTest.save, [updateState, tenantId]);
  const deleteBcdrDrTest = useCallback(drTest.del, [updateState, tenantId]);
  const saveBcdrPenTest = useCallback(penTest.save, [updateState, tenantId]);
  const deleteBcdrPenTest = useCallback(penTest.del, [updateState, tenantId]);

  return {
    saveBIA,
    overrideAssetCriticality,
    deleteBIA,
    saveBcdrPlan,
    deleteBcdrPlan,
    saveBcdrRunbook,
    deleteBcdrRunbook,
    saveBcdrRunbookRun,
    deleteBcdrRunbookRun,
    saveBcdrBackup,
    deleteBcdrBackup,
    saveBcdrDrTest,
    deleteBcdrDrTest,
    saveBcdrPenTest,
    deleteBcdrPenTest,
  };
}
